import httpService from "../services/httpService";

// Token refresh utility for maintaining authentication
// Automatically refreshes access tokens on a schedule and when tab becomes visible

// Refresh interval in milliseconds (90 minutes - stays ahead of 2-hour token expiration)
const TOKEN_REFRESH_INTERVAL = 90 * 60 * 1000;

// Maximum consecutive failures before triggering logout
const MAX_CONSECUTIVE_FAILURES = 3;

// Minimum time between visibility-triggered refreshes (prevents rapid refresh on tab switching)
const VISIBILITY_REFRESH_COOLDOWN = 30 * 1000; // 30 seconds
const shouldLog = process.env.NODE_ENV !== "test";

interface TokenRefreshResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  message?: string;
}

class TokenRefreshManager {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isRefreshing = false;
  private refreshCallback?: (tokens: { accessToken: string; refreshToken?: string }) => void;
  private errorCallback?: (error: string) => void;
  private visibilityHandler: (() => void) | null = null;
  private consecutiveFailures = 0;
  private lastVisibilityRefresh = 0; // Timestamp of last visibility-triggered refresh
  private generation = 0;

  /**
   * Start the token refresh schedule
   * @param refreshCallback - Callback to update tokens in auth context
   * @param errorCallback - Callback to handle refresh errors (e.g., logout user)
   */
  start(
    refreshCallback: (tokens: { accessToken: string; refreshToken?: string }) => void,
    errorCallback?: (error: string) => void
  ): void {
    if (this.intervalId) {
      console.warn("Token refresh is already running");
      return;
    }

    this.generation += 1;

    this.refreshCallback = refreshCallback;
    this.errorCallback = errorCallback;
    this.consecutiveFailures = 0;
    this.lastVisibilityRefresh = 0;

    // Start the interval for periodic refresh
    this.intervalId = setInterval(() => {
      this.refreshTokens();
    }, TOKEN_REFRESH_INTERVAL);

    // Add visibility change listener to refresh when tab becomes visible
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        // Enforce cooldown to prevent rapid refresh on tab switching
        if (now - this.lastVisibilityRefresh >= VISIBILITY_REFRESH_COOLDOWN) {
          if (shouldLog) {
            console.log("Tab became visible, refreshing tokens...");
          }
          this.lastVisibilityRefresh = now;
          this.refreshTokens();
        } else {
          if (shouldLog) {
            console.log("Tab became visible, but cooldown not elapsed - skipping refresh");
          }
        }
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);

    if (shouldLog) {
      console.log(
        `Token refresh started - will refresh every ${TOKEN_REFRESH_INTERVAL / 1000 / 60} minutes and on tab visibility`
      );
    }
  }

  /**
   * Stop the token refresh schedule
   */
  stop(): void {
    // Invalidate any response already in flight. Account activation replaces
    // both tokens, and an older scheduled refresh must never overwrite them.
    this.generation += 1;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Remove visibility listener
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }

    this.consecutiveFailures = 0;
    this.isRefreshing = false;
    if (shouldLog) {
      console.log("Token refresh stopped");
    }
  }

  /**
   * Check if token refresh is currently active
   */
  isActive(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Manually trigger a token refresh
   */
  async refreshNow(): Promise<boolean> {
    return await this.refreshTokens();
  }

  /**
   * Internal method to refresh tokens
   */
  private async refreshTokens(): Promise<boolean> {
    if (this.isRefreshing) {
      if (shouldLog) {
        console.log("Token refresh already in progress, skipping...");
      }
      return false;
    }

    const generation = this.generation;
    this.isRefreshing = true;

    try {
      const response = await this.callRefreshTokenAPI();

      if (generation !== this.generation) return false;

      // Success requires at least an access token; refresh token is optional (may reuse existing)
      if (response.success && response.accessToken) {
        // Notify the auth context to handle token storage
        if (this.refreshCallback) {
          const tokens: { accessToken: string; refreshToken?: string } = { accessToken: response.accessToken };
          if (response.refreshToken) {
            tokens.refreshToken = response.refreshToken;
          }
          this.refreshCallback(tokens);
        }

        // Reset failure counter on success
        this.consecutiveFailures = 0;
        if (shouldLog) {
          console.log("Tokens refreshed successfully");
        }
        return true;
      } else {
        console.error("Token refresh failed:", response.message);
        this.handleRefreshError(response.message || "Token refresh failed");
        return false;
      }
    } catch (error) {
      if (generation !== this.generation) return false;
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Token refresh network error:", errorMessage);
      this.handleRefreshError(`Network error: ${errorMessage}`);
      return false;
    } finally {
      if (generation === this.generation) this.isRefreshing = false;
    }
  }

  /**
   * Call the refresh token API
   * According to OpenAPI spec: POST /v2/auth/refresh-token returns { token }
   */
  private async callRefreshTokenAPI(): Promise<TokenRefreshResponse> {
    try {
      const response = await httpService.refreshToken<{
        token: string;
        accessToken?: string; // Some implementations may return accessToken
        refreshToken?: string; // Some implementations may return refreshToken
      }>();
      const data = response.data;

      // OpenAPI spec returns { token }, but some implementations may return { accessToken, refreshToken }
      // Handle both cases
      const accessToken = data.accessToken || data.token;
      const refreshToken = data.refreshToken;

      if (!accessToken) {
        return {
          success: false,
          message: "No token received from refresh endpoint",
        };
      }
      
      return {
        success: true,
        accessToken,
        refreshToken: refreshToken,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Token refresh failed";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Handle refresh errors with retry tolerance
   * Only triggers logout after multiple consecutive failures
   */
  private handleRefreshError(error: string): void {
    this.consecutiveFailures++;
    console.warn(`Token refresh failure ${this.consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}: ${error}`);

    // Only logout after multiple consecutive failures to handle transient network issues
    if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      console.error("Max consecutive refresh failures reached, triggering logout");
      this.stop();
      if (this.errorCallback) {
        this.errorCallback(error);
      }
    }
    // Otherwise, keep the interval running - next attempt may succeed
  }
}

// Export a singleton instance
export const tokenRefreshManager = new TokenRefreshManager();

// Export the class for testing purposes
export { TokenRefreshManager };

// Export utility functions
export const startTokenRefresh = (
  refreshCallback: (tokens: { accessToken: string; refreshToken?: string }) => void,
  errorCallback?: (error: string) => void
): void => {
  tokenRefreshManager.start(refreshCallback, errorCallback);
};

export const stopTokenRefresh = (): void => {
  tokenRefreshManager.stop();
};

export const isTokenRefreshActive = (): boolean => {
  return tokenRefreshManager.isActive();
};

export const refreshTokensNow = async (): Promise<boolean> => {
  return await tokenRefreshManager.refreshNow();
};
